import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { CalendarDays, Clapperboard, Lock, Newspaper, Search, SlidersHorizontal, X, Youtube } from "lucide-react";
import {
  AddToCalendar, Btn, Card, Chip, Empty, Hand, JoinButton, List, P, PLANS, PLink, PortalShell, ProgressRing,
  Row, Section, Segmented, SessionChip, Sheet, Thumb, TierLock, usePortal, useCountdown,
  PROJECTS, PROJECT_KINDS, POSTS, SESSIONS, atLeast, fmtDay, fmtDur, fmtT, getProject, hasReplay, projectRuntime,
  relDay, sessionState, sessionsFor, timeLabel, upcomingSessions,
  type ChipTone, type Plan, type Post, type Project, type ProjectKind, type Role, type Session, type SessionState,
} from "./Portal";
import "./Classes.css";

/* Classes tab (P2 "Project rooms", research §5).
   - Projects: one card per monthly project. Phone: a month-by-month shelf
     (coming up · this month · earlier). Desktop: a filter rail (type, watched
     status) next to a card grid. Search in the header highlights matches in
     project text and also finds chapters, fix notes and supply items inside
     the classes, linking straight to the timestamp.
   - Schedule: every upcoming club class and free YouTube live, grouped by
     month, in Mountain time and the viewer's time, with Add to calendar.
   - Extras: the leftover long-form posts, read in a sheet.
   Prototype: all state is local; nothing is saved or sent. */

/* ═══════════════════════════ Shared helpers (also used by Project.tsx) ═══════════════════════════ */

/** True from 900px (the shell's desktop breakpoint). */
export function useWide() {
  const q = "(min-width: 900px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Wraps every match of q in <mark>. */
export function hl(text: string, q: string): ReactNode {
  const t = q.trim();
  if (!t) return text;
  return text.split(new RegExp(`(${esc(t)})`, "ig")).map((part, i) => (i % 2 ? <mark key={i} className="cl-mark">{part}</mark> : part));
}
const has = (text: string | undefined, q: string) => !!text && text.toLowerCase().includes(q.trim().toLowerCase());

export const KIND_ONE: Record<ProjectKind, string> = { garment: "Garment", bag: "Bag", hat: "Hat", quilt: "Quilted" };

export type ProjectStatus = { label: string; tone: ChipTone; session?: Session; state?: SessionState; short: string };
/** Where a project is at `now`: live / replay processing / class tonight / next class / replays ready. */
export function projectStatus(p: Project, now: Date, tz: string): ProjectStatus {
  const st = sessionsFor(p.id).filter(x => !x.recorded).map(x => ({ x, s: sessionState(x, now) }));
  const find = (s: SessionState) => st.find(a => a.s === s);
  const live = find("live");
  if (live) return { label: `${live.x.label} is live now`, short: "Live now", tone: "live", session: live.x, state: "live" };
  const proc = find("processing");
  if (proc) return { label: `${proc.x.label} replay processing`, short: "Replay processing", tone: "peri", session: proc.x, state: "processing" };
  const tonight = find("tonight");
  if (tonight) return { label: `${tonight.x.label} tonight`, short: `${tonight.x.label} tonight`, tone: "gold", session: tonight.x, state: "tonight" };
  const next = find("upcoming");
  const anyReplay = st.some(a => a.s === "replay");
  if (next && !anyReplay) {
    const d = fmtDay(next.x.start, tz, { month: "short", day: "numeric" });
    return { label: `Starts ${d}`, short: `Starts ${d}`, tone: "neutral", session: next.x, state: "upcoming" };
  }
  if (next) {
    const r = relDay(next.x.start, now, tz);
    const d = ["Tonight", "Today", "Tomorrow"].includes(r) ? r.toLowerCase() : r;
    return { label: `${next.x.label} ${d}`, short: `${next.x.label} ${d}`, tone: "gold", session: next.x, state: "upcoming" };
  }
  return { label: "All replays ready", short: "Replays ready", tone: "teal" };
}

export type WatchPick = { session: Session; kind: "resume" | "start" | "rewatch" };
/** What a member should watch next in a project: a fresh replay they haven't started, else the furthest-along
    class they're partway through (people move forward through a project, so a 2-minute peek at the prep
    shouldn't win over being two-thirds into Class 1), else the first unstarted, else a rewatch. */
export function nextToWatch(p: Project, now: Date): WatchPick | undefined {
  const reps = sessionsFor(p.id).filter(x => x.replay && hasReplay(x, now));
  if (!reps.length) return undefined;
  const done = (x: Session) => (x.progress ?? 0) >= x.replay!.length - 30;
  const fresh = reps.filter(x => !x.recorded && !(x.progress ?? 0) && now.getTime() - x.start.getTime() < 2 * 864e5).pop();
  if (fresh) return { session: fresh, kind: "start" };
  const resume = reps.filter(x => (x.progress ?? 0) > 0 && !done(x)).pop();
  if (resume) return { session: resume, kind: "resume" };
  const start = reps.find(x => !(x.progress ?? 0));
  if (start) return { session: start, kind: "start" };
  return { session: reps[0], kind: "rewatch" };
}

type Watched = "all" | "new" | "progress" | "done";
const watchedOf = (p: Project, now: Date): Exclude<Watched, "all"> => {
  const r = projectRuntime(p, now);
  return r.ratio >= 0.98 ? "done" : r.watched > 0 ? "progress" : "new";
};

/** The plan a post needs, for the tier-named lock. */
const postPlan = (need: Role): Plan => (need === "visitor" ? "free" : need === "chelsea" ? "member" : need);

/** A post opened in a sheet (no post route in the prototype). Locked posts show the teaser and a tier-named lock. */
export function PostSheet({ post, onClose }: { post: Post | null; onClose: () => void }) {
  const { role } = usePortal();
  if (!post) return null;
  const ok = atLeast(role, post.need);
  const proj = getProject(post.projectId);
  return <Sheet open onClose={onClose} title={post.title}>
    <article className="cl-post">
      {post.cover && <img className="cl-post-cover" src={post.cover} alt="" />}
      <p className="cl-post-meta">{fmtDay(post.at, "America/Denver", { month: "short", day: "numeric" })} · {post.minutes} min read{proj ? ` · ${proj.title}` : ""}</p>
      {ok ? post.body.map((b, i) => <p key={i}>{b}</p>) : <>
        <p>{post.excerpt}</p>
        <TierLock thing={post.need === "member" ? "replays" : "posts"} need={postPlan(post.need)} force
          body={`This post is for ${PLANS[postPlan(post.need)].name}${post.need === "member" ? "s" : "s and Members"}.`} />
      </>}
      {ok && <p className="cl-post-foot">{post.likes} likes · {post.comments} comments</p>}
    </article>
  </Sheet>;
}

/* ═══════════════════════════ Project cards ═══════════════════════════ */

function ProjectCard({ p, q, featured }: { p: Project; q: string; featured?: boolean }) {
  const { now, tz, can } = usePortal();
  const r = projectRuntime(p, now);
  const st = projectStatus(p, now, tz);
  const ss = sessionsFor(p.id);
  const pending = ss.some(x => !x.recorded && sessionState(x, now) !== "replay");
  const hasPrep = ss.some(x => x.label === "Prep");
  const count = `${hasPrep ? "Prep + " : ""}${r.classes} classes`;
  const runtime = r.total ? ` · ${fmtDur(r.total)}${pending ? " so far" : ""}` : "";
  const showTeaser = q.trim() && (has(p.teaser, q) || has(p.subtitle, q)) && !has(p.title, q);
  const pct = Math.round(r.ratio * 100);
  return <Card to={P.project(p.id)} pad="none" className={`cl-card${featured ? " is-featured" : ""}`}>
    <span className="cl-card-img">
      <img src={p.hero} alt={p.heroAlt} loading="lazy" />
      {featured && <Chip tone={st.tone} className="cl-card-flag">{st.short}</Chip>}
    </span>
    <span className="cl-card-body">
      <span className="cl-card-text">
        <span className="cl-card-kicker">{p.monthLabel.replace(" 2026", "")} · {KIND_ONE[p.kind]}</span>
        <span className="cl-card-title">{hl(p.title, q)}</span>
        {showTeaser
          ? <span className="cl-card-meta">{hl(has(p.subtitle, q) ? p.subtitle : p.teaser, q)}</span>
          : <span className="cl-card-meta">{count}{runtime}</span>}
        {!featured && st.tone !== "teal" && <span className="cl-card-chips"><Chip tone={st.tone}>{st.short}</Chip></span>}
      </span>
      {can("replays") && r.total > 0 && <ProgressRing value={r.ratio} size={44} showPct label={`${pct}% watched`} className="cl-card-ring" />}
      {!can("replays") && r.total > 0 && <span className="cl-card-lock" title={`Replays: ${PLANS.member.tag}`}><Lock size={14} aria-hidden="true" /></span>}
    </span>
  </Card>;
}

type Hit = { key: string; kind: "Chapter" | "Fix note" | "Supply" ; text: string; where: string; to: string };
function deepHits(q: string, now: Date): Hit[] {
  const t = q.trim();
  if (t.length < 2) return [];
  const out: Hit[] = [];
  for (const p of PROJECTS) {
    for (const x of sessionsFor(p.id)) {
      if (!x.replay || !hasReplay(x, now)) continue;
      for (const c of x.replay.chapters) if (has(c.label, t)) out.push({ key: `${x.id}-c${c.t}`, kind: "Chapter", text: c.label, where: `${p.title} · ${x.label} · ${fmtT(c.t)}`, to: `${P.session(x.id)}?at=${c.t}` });
      for (const f of x.replay.fixNotes) if (has(f.text, t)) out.push({ key: `${x.id}-f${f.t}`, kind: "Fix note", text: f.text, where: `${p.title} · ${x.label} · ${fmtT(f.t)}`, to: `${P.session(x.id)}?at=${f.t}` });
    }
    const seen = new Set<string>();
    for (const it of [...p.supplies.pattern, ...p.supplies.chelseaUsed]) if (has(it.name, t) && !seen.has(it.name.toLowerCase()) && seen.add(it.name.toLowerCase())) out.push({ key: it.id, kind: "Supply", text: it.name, where: `${p.title} · supply list`, to: `${P.project(p.id)}#supplies` });
  }
  return out;
}

function Filters({ kinds, setKinds, watched, setWatched, counts, showWatched }: {
  kinds: Set<ProjectKind>; setKinds: (k: Set<ProjectKind>) => void; watched: Watched; setWatched: (w: Watched) => void;
  counts: { kind: Record<string, number>; watched: Record<string, number> }; showWatched: boolean;
}) {
  const toggle = (k: ProjectKind) => { const n = new Set(kinds); if (n.has(k)) n.delete(k); else n.add(k); setKinds(n); };
  const W: { v: Watched; l: string }[] = [{ v: "all", l: "Any" }, { v: "new", l: "Not started" }, { v: "progress", l: "In progress" }, { v: "done", l: "Watched" }];
  return <div className="cl-filters">
    <fieldset className="cl-fset">
      <legend className="pt-kicker">Project type</legend>
      {PROJECT_KINDS.map(k => <label key={k.key} className="cl-check">
        <input type="checkbox" checked={kinds.has(k.key)} onChange={() => toggle(k.key)} />
        <span>{k.label}</span><small>{counts.kind[k.key] ?? 0}</small>
      </label>)}
    </fieldset>
    {showWatched && <fieldset className="cl-fset">
      <legend className="pt-kicker">Watched</legend>
      {W.map(w => <label key={w.v} className="cl-check is-radio">
        <input type="radio" name="cl-watched" checked={watched === w.v} onChange={() => setWatched(w.v)} />
        <span>{w.l}</span>{w.v !== "all" && <small>{counts.watched[w.v] ?? 0}</small>}
      </label>)}
    </fieldset>}
  </div>;
}

function ProjectsView({ q, setQ }: { q: string; setQ: (s: string) => void }) {
  const { now, can, role } = usePortal();
  const wide = useWide();
  const [kinds, setKinds] = useState<Set<ProjectKind>>(new Set());
  const [watched, setWatched] = useState<Watched>("all");
  const [sheet, setSheet] = useState(false);
  const showWatched = can("replays");
  const t = q.trim();

  const textMatch = (p: Project) => !t || [p.title, p.subtitle, p.teaser, p.pattern.designer, p.monthLabel].some(s => has(s, t));
  const base = PROJECTS.filter(textMatch);
  const list = base.filter(p => (!kinds.size || kinds.has(p.kind)) && (!showWatched || watched === "all" || watchedOf(p, now) === watched));
  const counts = {
    kind: Object.fromEntries(PROJECT_KINDS.map(k => [k.key, base.filter(p => p.kind === k.key).length])),
    watched: Object.fromEntries((["new", "progress", "done"] as const).map(w => [w, base.filter(p => watchedOf(p, now) === w).length])),
  };
  const hits = useMemo(() => deepHits(t, now), [t, now]);
  const active = kinds.size + (showWatched && watched !== "all" ? 1 : 0);
  const clear = () => { setKinds(new Set()); setWatched("all"); };

  const groups: { key: string; title: string; items: Project[] }[] = [
    { key: "now", title: "This month", items: list.filter(p => p.status === "current") },
    { key: "next", title: "Coming up", items: list.filter(p => p.status === "announced") },
    { key: "past", title: "Earlier", items: list.filter(p => p.status === "past") },
  ].filter(g => g.items.length);
  const filtersUI = <Filters kinds={kinds} setKinds={setKinds} watched={watched} setWatched={setWatched} counts={counts} showWatched={showWatched} />;

  return <div className="cl-browse">
    {wide && <aside className="cl-rail" aria-label="Filters">
      {filtersUI}
      {active > 0 && <Btn variant="ghost" size="sm" onClick={clear}>Clear filters</Btn>}
    </aside>}

    <div className="cl-results">
      {!can("replays") && <div className="cl-upsell">
        <Chip tone="lock" icon={<Lock size={12} aria-hidden="true" />}>{PLANS.member.tag}</Chip>
        <p>Look around every project. {role === "visitor" ? "Live classes and replays come with Member." : "Upgrade to sew along live and watch every replay."}</p>
        <JoinButton need="member" size="sm" short={role !== "visitor"} />
      </div>}

      {!wide && <div className="cl-phonebar">
        <Btn variant="secondary" size="sm" icon={<SlidersHorizontal size={16} aria-hidden="true" />} onClick={() => setSheet(true)} aria-haspopup="dialog">
          Filter{active ? ` · ${active}` : ""}
        </Btn>
        {[...kinds].map(k => <button key={k} type="button" className="cl-pill" onClick={() => { const n = new Set(kinds); n.delete(k); setKinds(n); }}>
          {PROJECT_KINDS.find(x => x.key === k)!.label}<X size={14} aria-label="Remove" />
        </button>)}
        {showWatched && watched !== "all" && <button type="button" className="cl-pill" onClick={() => setWatched("all")}>
          {({ new: "Not started", progress: "In progress", done: "Watched" } as const)[watched]}<X size={14} aria-label="Remove" />
        </button>}
      </div>}

      {t && <p className="cl-count" aria-live="polite">{list.length} {list.length === 1 ? "project" : "projects"}{hits.length ? ` · ${hits.length} ${hits.length === 1 ? "match" : "matches"} inside classes` : ""} for “{t}”</p>}

      {list.length === 0 && <Empty icon={<Search size={22} aria-hidden="true" />} title="No projects match"
        body={t ? `Nothing called “${t}”${active ? " with these filters" : ""}.` : "Try fewer filters."}
        action={<Btn variant="secondary" size="sm" onClick={() => { clear(); setQ(""); }}>Show all projects</Btn>} />}

      {wide
        ? list.length > 0 && <div className="cl-grid">{list.map(p => <ProjectCard key={p.id} p={p} q={t} />)}</div>
        : groups.map(g => <section key={g.key} className="cl-shelf" aria-labelledby={`cl-g-${g.key}`}>
            <h2 className="cl-shelf-h" id={`cl-g-${g.key}`}>{g.title}</h2>
            <div className="cl-shelf-list">{g.items.map(p => <ProjectCard key={p.id} p={p} q={t} featured={g.key === "now"} />)}</div>
          </section>)}

      {hits.length > 0 && <Section title="Inside the classes" kicker="Chapters, fix notes & supplies">
        <List>
          {hits.slice(0, 12).map(h => <Row key={h.key} to={h.to}
            lead={<span className={`cl-hit-k is-${h.kind === "Chapter" ? "ch" : h.kind === "Fix note" ? "fix" : "sup"}`}>{h.kind}</span>}
            title={hl(h.text, t)} meta={h.where}
            trail={h.kind !== "Supply" && !can("replays") ? <Lock size={14} aria-label="Members" /> : undefined} />)}
        </List>
      </Section>}
    </div>

    <Sheet open={sheet} onClose={() => setSheet(false)} title="Filter projects"
      footer={<div className="cl-sheet-foot"><Btn variant="ghost" onClick={clear}>Clear</Btn><Btn onClick={() => setSheet(false)}>Show {list.length} {list.length === 1 ? "project" : "projects"}</Btn></div>}>
      {filtersUI}
    </Sheet>
  </div>;
}

/* ═══════════════════════════ Schedule ═══════════════════════════ */

function DateTile({ s, tone }: { s: Session; tone: string }) {
  const { tz } = usePortal();
  return <span className={`cl-date ${tone}`} aria-hidden="true">
    <small>{fmtDay(s.start, tz, { weekday: "short" })}</small>
    <b>{fmtDay(s.start, tz, { day: "numeric" })}</b>
  </span>;
}

function Starts({ at }: { at: Date }) {
  const c = useCountdown(at);
  return <span className="cl-starts">Starts in {c.label}</span>;
}

/** Free YouTube lives share one generic blurb; repeating it on every row is noise. */
const GENERIC_YT = /^Free for everyone on YouTube/;

function SchedItem({ s }: { s: Session }) {
  const { now, can, tz } = usePortal();
  const state = sessionState(s, now);
  const club = s.kind === "club";
  const p = getProject(s.projectId);
  const ok = can("live");
  const title = club ? `${p?.title ?? ""} · ${s.label}` : s.title;
  const link = club ? (ok && state !== "upcoming" ? P.session(s.id) : P.project(s.projectId!)) : undefined;
  const tone = state === "live" ? "is-live" : state === "tonight" ? "is-tonight" : club ? "is-club" : "is-yt";
  return <li className={`cl-sched ${tone}`}>
    <DateTile s={s} tone={tone} />
    <div className="cl-sched-main">
      <div className="cl-sched-chips">
        {club ? <Chip tone="teal" icon={<Clapperboard size={13} aria-hidden="true" />}>Club class</Chip>
          : <Chip tone="outline" icon={<Youtube size={14} aria-hidden="true" />}>Free on YouTube</Chip>}
        {state !== "upcoming" && <SessionChip state={state} />}
        {club && !ok && <Chip tone="lock" icon={<Lock size={12} aria-hidden="true" />}>{PLANS.member.tag}</Chip>}
      </div>
      {link ? <PLink to={link} className="cl-sched-title">{title}</PLink>
        : <a href={s.url} target="_blank" rel="noreferrer" className="cl-sched-title">{title}</a>}
      {/* The date tile already carries the day, so the line leads with the time. */}
      <p className="cl-sched-meta">
        {(state === "tonight" || state === "live" || state === "processing") && <>{relDay(s.start, now, tz)} · </>}
        {timeLabel(s.start, tz)} <span className="cl-nowrap">· {fmtDur(s.minutes * 60)}</span>
      </p>
      {s.blurb && !(s.kind === "youtube" && GENERIC_YT.test(s.blurb)) && <p className="cl-sched-blurb">{s.blurb}</p>}
    </div>
    <div className="cl-sched-act">
        {state === "live" && club && ok && <Btn variant="live" size="sm" to={P.session(s.id)}>Join live</Btn>}
        {state === "live" && !club && <Btn variant="primary" size="sm" href={s.url}>Watch on YouTube</Btn>}
        {state === "live" && club && !ok && <JoinButton need="member" size="sm" />}
        {state === "tonight" && <Starts at={s.start} />}
        {(state === "upcoming" || state === "tonight") && (!club || ok) && <AddToCalendar session={s} variant="quiet" size="sm" label="Add to calendar" />}
        {state === "processing" && <span className="cl-starts">Replay up by morning. We'll let you know.</span>}
    </div>
  </li>;
}

function ScheduleView() {
  const { now, tz, can, viewer } = usePortal();
  const current = SESSIONS.filter(x => !x.recorded && ["live", "processing"].includes(sessionState(x, now)));
  const next = upcomingSessions(now, 12);
  const months = new Map<string, Session[]>();
  for (const x of next) {
    const k = fmtDay(x.start, tz, { month: "long", year: "numeric" });
    months.set(k, [...(months.get(k) ?? []), x]);
  }
  const zone = viewer?.tzAbbr === "MT" ? "Mountain time" : `your time${viewer ? ` (${viewer.tzAbbr})` : ""} too`;
  return <div className="cl-schedule">
    <div className="cl-sched-intro">
      <p><CalendarDays size={18} aria-hidden="true" /> <span>Club classes: the 2nd Tuesday and the Thursday after. Free YouTube lives most other Tuesdays. All 7 PM MT, shown in {zone}.</span></p>
      {can("calendarFeed")
        ? <Btn variant="secondary" size="sm" to={`${P.me}#calendar`}>Subscribe to every class</Btn>
        : <span className="cl-feed"><span>Calendar feed of every class</span><TierLock thing="calendarFeed" variant="inline" /></span>}
    </div>
    {current.length > 0 && <section className="cl-sched-group" aria-labelledby="cl-s-now">
      <h2 className="cl-shelf-h" id="cl-s-now">Right now</h2>
      <ul className="cl-sched-list">{current.map(x => <SchedItem key={x.id} s={x} />)}</ul>
    </section>}
    {[...months].map(([m, xs]) => <section key={m} className="cl-sched-group" aria-labelledby={`cl-s-${m}`}>
      <h2 className="cl-shelf-h" id={`cl-s-${m}`}>{m}</h2>
      <ul className="cl-sched-list">{xs.map(x => <SchedItem key={x.id} s={x} />)}</ul>
    </section>)}
    <p className="cl-sched-note">Later dates go up once Chelsea sets the next project. No live on Oct 20: Chelsea's at Stitches Night Out in person.</p>
  </div>;
}

/* ═══════════════════════════ Extras ═══════════════════════════ */

function ExtrasView() {
  const { role } = usePortal();
  const [open, setOpen] = useState<Post | null>(null);
  const posts = [...POSTS].sort((a, b) => b.at.getTime() - a.at.getTime());
  return <>
    <List label="Extras">
      {posts.map(p => {
        const ok = atLeast(role, p.need);
        const proj = getProject(p.projectId);
        return <Row key={p.id} onClick={() => setOpen(p)}
          lead={<Thumb src={p.cover ?? "/img/chelsea.jpeg"} ratio="1/1" width={56} />}
          title={p.title}
          meta={`${fmtDay(p.at, "America/Denver", { month: "short", day: "numeric" })} · ${p.minutes} min read${proj ? ` · ${proj.title}` : ""}`}
          trail={ok ? undefined : <Chip tone="lock" icon={<Lock size={12} aria-hidden="true" />}>{PLANS[postPlan(p.need)].tag}</Chip>} />;
      })}
    </List>
    <p className="cl-sched-note"><Newspaper size={14} aria-hidden="true" /> Posts tied to a project also show on its page.</p>
    <PostSheet post={open} onClose={() => setOpen(null)} />
  </>;
}

/* ═══════════════════════════ Screen ═══════════════════════════ */

type View = "projects" | "schedule" | "extras";
export function PortalClasses() {
  const { now } = usePortal();
  const [sp, setSp] = useSearchParams();
  const v = sp.get("view");
  const view: View = v === "schedule" || v === "extras" ? v : "projects";
  const setView = (n: View) => setSp(prev => { const x = new URLSearchParams(prev); if (n === "projects") x.delete("view"); else x.set("view", n); return x; }, { replace: true, preventScrollReset: true });
  const [q, setQ] = useState("");
  const upcoming = upcomingSessions(now, 12).length;

  const search = <label className="cl-search">
    <Search size={18} aria-hidden="true" />
    <input type="search" value={q} placeholder="Search projects, chapters, supplies" aria-label="Search classes"
      onChange={e => { setQ(e.target.value); if (view !== "projects") setView("projects"); }} />
    {q && <button type="button" onClick={() => setQ("")} aria-label="Clear search"><X size={16} /></button>}
  </label>;

  return <PortalShell active="classes" title="Classes" subtitle="Every monthly project, its classes and replays." actions={search} className="cl-root">
    <div className="cl-tabs">
      <Segmented value={view} onChange={setView} label="Classes view" options={[
        { value: "projects", label: "Projects", count: PROJECTS.length },
        { value: "schedule", label: "Schedule", count: upcoming },
        { value: "extras", label: "Extras" },
      ]} />
      {view === "projects" && <Hand as="span" className="cl-tabs-hand">one project a month!</Hand>}
    </div>
    {view === "projects" && <ProjectsView q={q} setQ={setQ} />}
    {view === "schedule" && <ScheduleView />}
    {view === "extras" && <ExtrasView />}
  </PortalShell>;
}
